import React, { useCallback, useEffect, useState } from 'react';
import { Segment } from '../../models/Segment';
import { ISegmentActions } from '../../models/ISegmentActions';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { Checkout } from '../../models/Checkout';
import { CheckoutSegmentsRuleEngineConfig } from '../../config/CheckoutSegmentRuleEngineConfig';
import { SegmentModal } from './segment-modal/SegmentModal';
import ThreeDotMenu from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import { RuleEngineV2 } from '@app/react-common/components/rule-engine-v2/RuleEngineV2';
import Button from '@app/react-common/components/button/Button';
import { validateRuleEngineState } from '@app/react-common/components/rule-engine-v2/utils/validation';
import { useAppContext } from '@app/react-common/AppContext';
import { Property } from '@app/react-common/components/rule-engine-v2/models/RuleEngineConfig';
import { usePdqCoreApi } from '@app/react-common/api/usePdqCoreApi';
import { getKeyByStringValue } from '@utils/string-utils';
import { countryCodeToDisplayNameMap } from '@pdqdev/pdq-shared/dist/utils/Country-utils';

export interface ISegmentsProps {
  segments: Segment[];
  actions: ISegmentActions;
  checkout: Checkout;
}

export const Segments: React.FC<ISegmentsProps> = ({ segments, actions, checkout }) => {
  const [segmentToRemove, setSegmentToRemove] = useState(null);
  const [showRenameSegmentModal, setShowRenameSegmentModal] = useState(-1);
  const [showSegmentValidationErrors, setShowSegmentValidationErrors] = useState(false);
  const { showToast, showFullPageLoader, emitAngularAction } = useAppContext();
  const failureHandler = useCallback(async () => {
    showToast({ message: `An error has ocurred. Please contact support if the problem persists.`, mode: 'danger' });
  }, []);
  const pdqCoreApi = usePdqCoreApi(failureHandler);

  useEffect(() => {
    for (const segment of segments) {
      if (actions.checkIfSegmentHasBeenEdited(checkout.id, segment.internalId)) {
        emitAngularAction({
          action: 'enable-navigate-away-guard',
          payload: { enabled: true },
          callback: () => {},
        });
        return;
      }
    }
    emitAngularAction({
      action: 'enable-navigate-away-guard',
      payload: { enabled: false },
      callback: () => {},
    });
  }, [segments]);

  return (
    <>
      {segments?.length > 0 &&
        segments.map(
          (segment) =>
            segment && (
              <React.Fragment key={`${checkout.id}-${segment.internalId}`}>
                <div className="segment">
                  <div className={`segment-header ${segment.expanded && 'header-expanded'}`}>
                    <div
                      className={`segment-header__expander ${segment.expanded ? 'rotate' : ''}`}
                      onClick={() => {
                        actions.expandSegment(checkout.id, segment.internalId);
                      }}></div>
                    <span
                      onClick={() => {
                        actions.expandSegment(checkout.id, segment.internalId);
                      }}
                      className="segment-header__title">
                      {segment.name}
                    </span>
                    {segment?.rules && actions.checkIfSegmentHasBeenEdited(checkout.id, segment.internalId) && (
                      <Button
                        className="segment-header__cancel-button"
                        onClick={() => {
                          actions.cancelSegmentEdits(checkout.id, segment.internalId);
                        }}
                        text={'Cancel'}
                        size={'medium'}
                        variant={'secondary'}
                      />
                    )}
                    {segment?.rules && actions.checkIfSegmentHasBeenEdited(checkout.id, segment.internalId) && (
                      <Button
                        className="segment-header__save-button"
                        onClick={async () => {
                          if (
                            segment?.rules?.ruleGroups?.length === 0 ||
                            segment.rules.ruleGroups[0].rules?.length === 0
                          ) {
                            showToast({
                              message: 'Please add at least one rule to the segment',
                              mode: 'warning',
                            });
                            return;
                          }

                          if (!validateRuleEngineState(segment.rules)) {
                            setShowSegmentValidationErrors(true);
                            showToast({
                              message: 'Please check the rules and rule groups. They cannot be empty or invalid.',
                              mode: 'warning',
                            });
                            return;
                          }
                          if (checkout.isLive) {
                            showToast({
                              message:
                                'You cannot edit segment rules while checkout is live. Please disable the checkout first.',
                              mode: 'warning',
                            });
                            return;
                          }

                          showFullPageLoader(true);
                          await actions.saveSegmentToApi(checkout.id, segment.internalId);
                          showFullPageLoader(false);
                        }}
                        text={'Save'}
                        size={'medium'}
                        variant={'primary'}
                      />
                    )}
                    <ThreeDotMenu
                      items={[
                        {
                          label: 'Rename',
                          onClick: () => {
                            setShowRenameSegmentModal(segment.internalId);
                          },
                        },
                        {
                          label: 'Delete',
                          onClick: () => {
                            setSegmentToRemove(segment);
                          },
                          variant: 'danger',
                        },
                      ]}
                    />
                  </div>
                  <div className={`segment-content ${segment.expanded ? 'seg-expanded' : 'seg-not-expanded'}`}>
                    <RuleEngineV2
                      key={`rule-engine-${checkout.id}-${segment.internalId}-${segment.rerender}`}
                      config={CheckoutSegmentsRuleEngineConfig}
                      initialState={
                        segment?.rules
                          ? segment.rules
                          : {
                              matchAllRuleGroups: true,
                              ruleGroups: [],
                            }
                      }
                      bindings={{
                        onChange: (state) => {
                          actions.updateSegmentState(checkout.id, segment.internalId, state);
                        },
                        showValidationErrors: showSegmentValidationErrors,
                        fetchAutocompleteValues: async (property: Property, input: string = ''): Promise<string[]> => {
                          // destination code autocomplete
                          if (property === Property.country) {
                            return Object.values(countryCodeToDisplayNameMap);
                          }
                          const returnObject = await pdqCoreApi.get(
                            `/conditions/autocomplete/fetch?input=${encodeURIComponent(input)}&autocompleteBy=${getKeyByStringValue(Property, property)}`,
                          );
                          return returnObject.data;
                        },
                        showToast: showToast,
                      }}
                    />
                  </div>
                </div>
                {segments.indexOf(segment) < segments.length - 1 && (
                  <div key={`splitter-${checkout.id}-${segment.internalId}`} className="segment-splitter">
                    OR
                  </div>
                )}
                {showRenameSegmentModal === segment.internalId && (
                  <SegmentModal
                    segmentName={segment.name}
                    isOpen={showRenameSegmentModal === segment.internalId}
                    onRenameSuccess={async (segmentFormData) => {
                      if (checkout.isLive) {
                        showToast({
                          message:
                            'You cannot rename a segment while checkout is live. Please disable the checkout first.',
                          mode: 'warning',
                        });
                        setShowRenameSegmentModal(-1);
                        return;
                      }
                      await actions.updateSegmnetName(checkout.id, segment.internalId, segmentFormData.name);
                      setShowRenameSegmentModal(-1);
                    }}
                    onClose={() => {
                      setShowRenameSegmentModal(-1);
                    }}
                  />
                )}
              </React.Fragment>
            ),
        )}
      {segmentToRemove != null && (
        <ConfirmationModal
          title={`Are you sure you want to remove this segment?`}
          buttonText={'Remove segment'}
          mainContent={`You are about to remove segment "${segmentToRemove.name}"`}
          isOpen={segmentToRemove != null}
          onClose={() => {
            setSegmentToRemove(null);
          }}
          onSuccess={() => {
            if (checkout.isLive) {
              showToast({
                message: 'You cannot remove a segment while checkout is live. Please disable the checkout first.',
                mode: 'warning',
              });
              setSegmentToRemove(null);
              return;
            }
            actions.removeSegment(checkout.id, segmentToRemove.internalId);
            setSegmentToRemove(null);
          }}
          buttonVariant={'danger'}
        />
      )}
    </>
  );
};
